/**
 * What a data tile draws once the viewer's choices apply. The Statistic
 * control narrows a builder tile's run to the chosen statistic (see
 * `applyControls`), so the tile, its header control and the exports draw
 * that statistic, not the author's `visual.statistic`, which the table may
 * no longer hold. The tile's view is its last result with its measures and
 * category display applied: tiles and exports draw the view, while the
 * Inspector lists categories from the raw result.
 */

import { useMemo } from "react"
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import type { NumberFormatSpec, TableModel } from "utils/tableModel"
import { formatCell } from "utils/tableModel"
import type {
  CategoryDisplay,
  DataTile,
  Measure,
  RowsDisplay,
  ScorecardTile,
  TableTile,
} from "features/Dashboard/types/dashboardDocument"
import type { TileControlState } from "./tileScript"
import { useDashboardStore } from "./dashboardStore"
import {
  applyMeasures,
  columnBases,
  heldStatistic,
  isPercentStat,
} from "./measures"
import { applyCategoryDisplay } from "./categoryDisplay"
import { modelCellColumn } from "./modelAdapters"
import { downRowsOf, withDownRows } from "./downRows"

/** The data tiles that draw one statistic of their own: charts and numbers. */
export type StatisticTile = Exclude<DataTile, TableTile | ScorecardTile>

/**
 * The statistic `tile` draws: the viewer's pick when the tile is a builder
 * tile (the only kind the control changes), else `visual.statistic`.
 */
export const effectiveStatistic = (
  tile: StatisticTile,
  controls?: TileControlState
): STATISTIC_TYPE =>
  (tile.source.kind === "builder" ? controls?.statistic : undefined) ??
  tile.visual.statistic

/**
 * The one statistic a tile's view is built in (its measures) and a
 * scorecard reads. A chart or number: the statistic it draws. A builder
 * table or scorecard: the viewer's pick, else the first percentage among
 * its statistics, else its first statistic. A script table or scorecard:
 * the first percentage `model` (its last result) holds, else the first
 * statistic it holds; col% before it has a result.
 */
export const viewStatistic = (
  tile: DataTile,
  controls?: TileControlState,
  model?: TableModel
): string => {
  if (tile.type !== "table" && tile.type !== "scorecard") {
    return effectiveStatistic(tile, controls)
  }
  if (tile.source.kind === "builder") {
    const { statistics } = tile.source
    return (
      controls?.statistic ??
      statistics.find((s) => isPercentStat(s)) ??
      statistics[0] ??
      STATISTIC_TYPE.COL_PERCENT
    )
  }
  if (!model) return STATISTIC_TYPE.COL_PERCENT
  const leaves =
    model.statAxis === "down" ? model.down.leaves : model.across.leaves
  const percent = leaves.find(
    (l) => l.stat !== undefined && isPercentStat(l.stat)
  )?.stat
  return heldStatistic(model, percent ?? STATISTIC_TYPE.COL_PERCENT)
}

/**
 * A transposed table shows its result as it is: its rows are the column
 * variable's categories, so measures and category display do not apply.
 */
const showsRawResult = (tile: DataTile): boolean =>
  tile.type === "table" && tile.visual.transposed

/**
 * Whether the result holds "n" only to weight a rolling average: a builder
 * tile with a rolling measure whose viewer picked another statistic keeps
 * "n" in its run (see `planTileRun`), but the viewer did not ask to see it.
 */
const baseOnlyForWeighting = (
  tile: DataTile,
  controls?: TileControlState
): boolean =>
  tile.source.kind === "builder" &&
  controls?.statistic !== undefined &&
  controls.statistic !== STATISTIC_TYPE.N &&
  tile.measures.some((m) => m.kind === "rolling")

/** `model` without its "n" rows (stats down); the measures have read them. */
const withoutBaseRows = (model: TableModel): TableModel =>
  model.statAxis === "down" &&
  model.down.leaves.some((l) => l.stat === STATISTIC_TYPE.N)
    ? withDownRows(
        model,
        downRowsOf(model).filter((r) => r.leaf.stat !== STATISTIC_TYPE.N)
      )
    : model

/**
 * The view: the measures added as the tile's rows ask, in `statistic` (a
 * table in "both" keeps every statistic of its categories), then, when "n"
 * was only kept to weight a rolling average, its "n" rows dropped, then
 * the category display.
 */
const composeView = (
  model: TableModel,
  view: {
    measures: Measure[]
    rows: RowsDisplay
    categories: CategoryDisplay
    statistic: string
    everyStatistic: boolean
    dropBase: boolean
  }
): TableModel => {
  const measured = applyMeasures(
    model,
    view.measures,
    view.rows,
    view.statistic,
    { everyStatistic: view.everyStatistic }
  )
  return applyCategoryDisplay(
    view.dropBase ? withoutBaseRows(measured) : measured,
    view.categories
  )
}

/**
 * The model `tile` shows: `model` (its last result) with its measures
 * added as `tile.display.rows` asks, then its category display applied.
 * `model` itself when the tile has neither, or is a transposed table.
 */
export const buildTileView = (
  tile: DataTile,
  model: TableModel,
  controls?: TileControlState
): TableModel =>
  showsRawResult(tile)
    ? model
    : composeView(model, {
        measures: tile.measures,
        rows: tile.display.rows,
        categories: tile.categories,
        statistic: viewStatistic(tile, controls, model),
        everyStatistic: tile.type === "table",
        dropBase: baseOnlyForWeighting(tile, controls),
      })

/**
 * `buildTileView` over the tile's runtime model and controls, recomputed
 * only when the model, the controls' statistic or the tile's measures,
 * rows or category display change. Undefined while the tile has no model.
 */
export const useTileView = (tile: DataTile): TableModel | undefined => {
  const model = useDashboardStore((s) => s.tiles[tile.id]?.model)
  const controls = useDashboardStore((s) => s.tileControls[tile.id])
  const statistic = viewStatistic(tile, controls, model)
  const dropBase = baseOnlyForWeighting(tile, controls)
  const everyStatistic = tile.type === "table"
  const raw = showsRawResult(tile)
  const { measures, categories } = tile
  const rows = tile.display.rows
  return useMemo(
    () =>
      model && !raw
        ? composeView(model, {
            measures,
            rows,
            categories,
            statistic,
            everyStatistic,
            dropBase,
          })
        : model,
    [
      model,
      raw,
      measures,
      rows,
      categories,
      statistic,
      everyStatistic,
      dropBase,
    ]
  )
}

const COUNT_FORMAT: NumberFormatSpec = {
  decimals: 0,
  thousands: true,
  percent: false,
}

/**
 * The base line of a KPI: `viewBase` (what `modelCell` found in the view)
 * when there is one, else the Totals "n" of the picked column in `raw`, the
 * tile's last result, since a view of measures has dropped the rows the
 * base is read from. Null when neither has a base.
 */
export const kpiBaseText = (
  raw: TableModel,
  view: TableModel,
  opts: { column: string | null; statistic: string },
  viewBase: string | null
): string | null => {
  if (viewBase) return viewBase
  const c = modelCellColumn(view, opts)
  const n = c >= 0 ? (columnBases(raw)[c] ?? null) : null
  return n === null
    ? null
    : formatCell({ raw: n, stat: "n", kind: "count", format: COUNT_FORMAT })
}
